import { useEffect, useState } from 'react'
import './Scheduler.scss'
import React from 'react'
import { useTranslation } from 'react-i18next'

interface IProps {
  /** Display date. */
  date: number

  /** SelectedDate. */
  selectedDate?: number

  /**
   * When the user selects a date.
   * @param date selected value.
   */
  onDateSelected?: (date: number) => void

  /** Month offset. By default 0. */
  offset?: number

  /** The minimal date that can be selected. */
  minDate?: number

  /** The maximal date that can be selected. */
  maxDate?: number
  combinedViewAutoUpdate?: boolean
  disableSelection?: boolean
}

interface IDay {
  /** Number of day. */
  number: number

  /** Type of day. */
  type: DayType

  /** Unix time. */
  date: number

  /** Day is selected. */
  selected: boolean
}

/**
 * Days are divided by type for convenience.
 */
enum DayType {
  NotAvailable = 'notAvailable',
  Available = 'available',
  Weekend = 'disabled',
}

// Week in JS starts on sunday.
const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']

const Scheduler = (props: IProps) => {
  const [days, setDays] = useState<IDay[]>([])
  const [selectedDay, setSelectedDay] = useState<number | undefined>()

  const { t } = useTranslation('common')
  useEffect(() => {
    setSelectedDay(props.selectedDate)
  }, [])
  // For Combined view returns scheduler needs extra state update that is manually triggered from parent CombinedScheduler.
  // When date is selected and saved in Delivery Scheduler && combinedView = true, combinedViewAutoUpdate is triggered once
  useEffect(() => {
    if (props.combinedViewAutoUpdate) setSelectedDay(props.selectedDate)
  }, [props.combinedViewAutoUpdate])

  useEffect(() => {
    generateScheduler()
  }, [props.date, selectedDay, props.disableSelection])

  const generateScheduler = () => {
    const days: IDay[] = []

    const displayDate = new Date(props.date)
    const selectedMonth = displayDate.getMonth() + 1
    const selectedYear = displayDate.getFullYear()
    const numberOfDays = new Date(selectedYear, selectedMonth, 0).getDate()

    // Selected single day.
    const selectedTime = new Date(selectedDay ?? 0).getTime()

    // Number of days in the prev month.
    const prevNumberOfDays = new Date(
      selectedMonth > 1 ? selectedYear : selectedYear - 1,
      selectedMonth > 1 ? selectedMonth - 1 : 11,
      0
    ).getDate()

    // Generate prev days.
    let date = new Date(selectedYear, selectedMonth - 1, 1)

    if (dayNames.indexOf(dayNames[date.getDay()]) !== 1) {
      let difference = dayNames.indexOf(dayNames[date.getDay()]) - 1
      difference = difference === -1 ? 6 : difference

      for (let x = prevNumberOfDays - difference + 1; x <= prevNumberOfDays; x++) {
        const day = new Date(
          selectedMonth === 1 ? selectedYear - 1 : selectedYear,
          selectedMonth === 1 ? 11 : selectedMonth - 2,
          x
        ).getDay()
        const type = day > 0 && day < 6 ? DayType.NotAvailable : DayType.Weekend
        const selected =
          new Date(
            selectedMonth === 1 ? selectedYear - 1 : selectedYear,
            selectedMonth === 1 ? 11 : selectedMonth - 2,
            x
          ).getTime() -
            selectedTime ===
          0

        days.push({
          number: x,
          type,
          date: 0,
          selected,
        })
      }
    }

    // Generate actual days.
    for (let x = 1; x <= numberOfDays; x++) {
      const day = new Date(selectedYear, selectedMonth - 1, x).getDay()
      const selected = new Date(selectedYear, selectedMonth - 1, x).getTime() - selectedTime === 0

      let type = day > 0 && day < 6 ? DayType.Available : DayType.Weekend

      if (type !== DayType.Weekend) {
        if (props.minDate && new Date(selectedYear, selectedMonth - 1, x).getTime() - props.minDate < 0) {
          type = DayType.NotAvailable
        }

        if (props.maxDate && new Date(selectedYear, selectedMonth - 1, x).getTime() - props.maxDate > 0) {
          type = DayType.NotAvailable
        }
        if (props.disableSelection) {
          type = DayType.NotAvailable
        }
      }

      days.push({
        number: x,
        type,
        date: new Date(selectedYear, selectedMonth - 1, x).getTime(),
        selected,
      })
    }

    // Generate next days.
    date = new Date(selectedYear, selectedMonth - 1, numberOfDays)

    if (dayNames.indexOf(dayNames[date.getDay()]) !== 0) {
      const difference = dayNames.length - dayNames.indexOf(dayNames[date.getDay()])

      for (let x = 1; x <= difference; x++) {
        const day = new Date(
          selectedMonth === 12 ? selectedYear + 1 : selectedYear,
          selectedMonth === 12 ? 0 : selectedMonth,
          x
        ).getDay()
        const type = day > 0 && day < 6 ? DayType.NotAvailable : DayType.Weekend
        const selected =
          new Date(
            selectedMonth === 12 ? selectedYear + 1 : selectedYear,
            selectedMonth === 12 ? 0 : selectedMonth,
            x
          ).getTime() -
            selectedTime ===
          0

        days.push({
          number: x,
          type,
          date: 0,
          selected,
        })
      }
    }
    setDays(days)
  }

  const body = () => {
    const trs: JSX.Element[] = []

    for (let x = 0; x < days.length / 7; x++) {
      const onClick = (day: IDay) => {
        if (day.type === DayType.Available) {
          setSelectedDay(day.date)

          if (props.onDateSelected) {
            props.onDateSelected(day.date)
          }
        }
      }

      trs.push(
        <tr key={x}>
          {days.slice(x * 7, x * 7 + 7).map((day, index) => (
            <td key={index} className={day.type} onClick={() => onClick(day)}>
              <div className={day.selected ? 'selected' : ''}>{day.number}</div>
            </td>
          ))}
        </tr>
      )
    }

    return trs
  }

  return (
    <>
      <table className="scheduler">
        <thead>
          <tr>
            {dayNames.slice(1, 6).map((day, index) => (
              <th key={index}>
                <span>{t(day)}</span>
              </th>
            ))}
            <th className="disabled">
              <span>{t(dayNames[6])}</span>
            </th>
            <th className="disabled">
              <span>{t(dayNames[0])}</span>
            </th>
          </tr>
        </thead>
        <tbody>{body()}</tbody>
      </table>
    </>
  )
}

export default Scheduler
